import classNames from "classnames";
import { Flex, Link, Tab, Tabs, Text } from "@jsq/ds/core";
import { DataStructureRoutingProvider } from "~/pages/Reporting/DataStructureRoutingContext";
import { useDataStructureRouting } from "~/pages/Reporting/useDataStructureRouting";
import { SPACING } from "~/styles/variables";
import {
  DataStructure,
  DataStructureSidebar,
  VALID_DATA_STRUCTURE_TABS,
} from "./components/DataStructure";
import { SystemTemplatesList } from "./components/SystemTemplatesList";
import * as styles from "./styles_vanilla.css";

type TabId = "system-templates" | "data-structure";

export const Settings = () => {
  const routing = useDataStructureRouting({
    dataStructureBasePath: "/reporting/settings/data-structures",
    parentPath: "/reporting/settings",
    validTabs: VALID_DATA_STRUCTURE_TABS,
  });

  const activeTab: TabId = routing.isDataStructureActive
    ? "data-structure"
    : "system-templates";

  const contentContainerClassName = classNames(styles.contentContainer, {
    [styles.dataStructureContentContainer]: activeTab === "data-structure",
  });

  const handleTabChange = (tab: TabId) => {
    if (tab === activeTab) return;
    if (tab === "data-structure") {
      routing.navigateToDataStructures();
    } else {
      routing.navigateToParent();
    }
  };

  const renderTabContent = () => {
    if (activeTab === "system-templates") {
      return <SystemTemplatesList />;
    }
    return <DataStructure arenaId={null} />;
  };

  return (
    <Flex flexDirection="column" className={styles.container}>
      <Flex
        flexDirection="column"
        gap={SPACING.LARGE}
        alignItems="flex-start"
        className={styles.header}
      >
        <Link to="/reporting" icon="arrow-left" variant="primary">
          Back to custom reports
        </Link>
        <Flex alignItems="center">
          <Text variant="h2">Reporting settings</Text>
        </Flex>
        <Tabs hasBottomBorder={false} hasPaddingInline={false}>
          <Tab
            text="System templates"
            active={activeTab === "system-templates"}
            onClick={() => handleTabChange("system-templates")}
            data-testid="system-templates-tab"
          />
          <Tab
            text="Data structure"
            active={activeTab === "data-structure"}
            onClick={() => handleTabChange("data-structure")}
            data-testid="data-structure-tab"
          />
        </Tabs>
      </Flex>
      <DataStructureRoutingProvider value={routing}>
        <Flex flexGrow={1} flexDirection="row">
          {activeTab === "data-structure" && (
            <Flex flexDirection="column" gap={SPACING.X_SMALL} className={styles.sidebar}>
              <DataStructureSidebar
                activeTab={routing.dataStructureType}
                onTabChange={routing.handleTypeChange}
              />
            </Flex>
          )}
          <Flex
            flexGrow={1}
            flexDirection="column"
            gap={14}
            className={contentContainerClassName}
          >
            {renderTabContent()}
          </Flex>
        </Flex>
      </DataStructureRoutingProvider>
    </Flex>
  );
};
